import { inject, Injectable } from "@angular/core";
import { Observable, ReplaySubject } from "rxjs";
import { map, tap } from "rxjs/operators";
import { ApiClientService } from "~app/common/services/api-client.service";
import { IRelateratHandelssortiment } from "~app/features/planera/model/IHandelssortiment";
import { HandelssortimentResponseDTO } from "../../../common/models/handelssortiment/HandelssortimentResponseDTO";

import {
    IHandelssortimentgrunddata,
    IHandelssortimentSearchParamsModel,
    IHandelssortimentsgrupp,
    IProdukt,
    ISearchHandelssortimentModel,
    ISearchViol3HandelssortimentModel,
    ISortiment
} from "./handelssortiment.models";

@Injectable({
    providedIn: "root"
})
export class HandelssortimentService {
    grunddataInitiated = false;
    grunddataSubject = new ReplaySubject<IHandelssortimentgrunddata>(null);

    private readonly apiClient = inject(ApiClientService);

    fetchFDIDokumentUrl(handelssortimentId: number, viol3: boolean): Observable<string> {
        return this.apiClient
            .get<string>(`handelssortiment/${handelssortimentId}/fdidokumenturl?viol3=${viol3}`);
    }

    getFordelningstyper(): Observable<string[]> {
        return this.getHandelssortimentBasData().pipe(
            map(bd => bd.fordelningstyper)
        );
    }

    getForseslagnaMattslag(): Observable<string[]> {
        return this.getHandelssortimentBasData().pipe(
            map(bd => bd.foreslagnaMattslag)
        );
    }

    getHandelssortimentBasData(): Observable<IHandelssortimentgrunddata> {
        if (!this.grunddataInitiated) {
            this.grunddataInitiated = true;
            this.reloadHandelssortimentBasData().subscribe(() => {
                this.grunddataInitiated = false;
            });
        }
        return this.grunddataSubject.asObservable();
    }

    getHandelssortimentById(id: number): Observable<HandelssortimentResponseDTO> {
        return this.apiClient.get<HandelssortimentResponseDTO>(`Produkt/GetHandelssortimentById/${id}`, true);
    }

    getHandelssortimentgruppLista(): Observable<IHandelssortimentsgrupp[]> {
        return this.getHandelssortimentBasData().pipe(
            map(bd => bd.handelssortimentsgruppLista)
        );
    }

    getProduktLista(handelssortimentGruppId: number): Observable<IProdukt[]> {
        const getCompatibleHandelssortimentsgrupper = (bd: IHandelssortimentgrunddata): IHandelssortimentsgrupp[] => {
            const source = bd.handelssortimentsgruppLista.filter(x => x.id === handelssortimentGruppId)[0];
            if (!source || !source.compatibleProduktHandelssortimentsgruppKoder) {
                return [];
            }
            return source.compatibleProduktHandelssortimentsgruppKoder
                .map(kod => bd.handelssortimentsgruppLista
                    .filter(handelssortimentsgrupp => handelssortimentsgrupp.kod === kod)[0])
                .filter(handelssortimentsgrupp => handelssortimentsgrupp);
        };

        return this.getHandelssortimentBasData().pipe(
            map(bd => getCompatibleHandelssortimentsgrupper(bd)
                .reduce((r, handelssortimentsgrupp) => {
                    if (handelssortimentsgrupp) {
                        r.push(...bd.produktLista.filter(produkt => produkt.handelssortimentsgruppId === handelssortimentsgrupp.id));
                    }
                    return r;
                }, new Array<IProdukt>())
            ));
    }

    getSortimentLista(): Observable<ISortiment[]> {
        return this.getHandelssortimentBasData().pipe(
            map(bd => bd.sortimentLista)
        );
    }

    reloadHandelssortimentBasData(): Observable<IHandelssortimentgrunddata> {
        return this.apiClient.get<IHandelssortimentgrunddata>("Produkt/GetHandelssortimentBasData", true)
            .pipe(tap(r => { this.grunddataSubject.next(r); }));
    }

    searchForHandelssortiment(handelssortimentSearchParams: IHandelssortimentSearchParamsModel): Observable<ISearchHandelssortimentModel[]> {
        return this.apiClient.get<ISearchHandelssortimentModel[]>("handelssortiment", false, handelssortimentSearchParams);
    }

    searchForRelaterbaraHandelssortiment(id: number, query: any): Observable<IRelateratHandelssortiment[]> {
        return this.apiClient
            .get<IRelateratHandelssortiment[]>(`Handelssortiment/SearchForRelaterbaraHandelssortiment?query=${encodeURIComponent(query)}&excludeId=${id ? id : ""}`, false);
    }

    searchForViolId(query: string): Observable<ISearchViol3HandelssortimentModel[]> {
        return this.apiClient.get<ISearchViol3HandelssortimentModel[]>("handelssortiment/SearchViol", false, {
            query: query
        });
    }
}
